import API from '@aws-amplify/api'
import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import MuiAlert from '@mui/material/Alert'
import SmoothLoadingIndicator from 'components/shared/SmoothLoadingIndicator'

import {
  COMPLETE_STATUS,
  PENDING_STATUS,
  CLOSED_STATUS,
  CANCELLED_STATUS,
} from 'enums/statuses'
import { PurchaseOrder } from 'interfaces/IPurchaseOrder'
import React, { useState } from 'react'
import ManagePurchaseOrder from './managePurchaseOrder'
import { checkAccess } from 'shared/auth'
import { IReduxState } from 'interfaces/IReduxState'
import { useSelector } from 'react-redux'
import ConfirmDialog from 'components/confirmDialog'
import ClosePurchaseOrderDialog from './closePurchaseOrderDialog'

const useStyles = makeStyles((theme) => ({
  labelColumn: {
    maxWidth: 'min-content',
  },
  expandableDetails: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  hiddenDetails: {
    paddingTop: 0,
  },
  productRow: {
    cursor: 'pointer',
  },
}))

export default function PurchaseOrderList() {
  const classes = useStyles()
  const [isSuccessSnackbarOpen, setIsSuccessSnackbarOpen] =
    React.useState(false)
  const [isFailureSnackbarOpen, setIsFailureSnackbarOpen] =
    React.useState(false)
  const userGroups = useSelector((state: IReduxState) => state.userGroups || [])
  const groupAccessMapping = useSelector(
    (state: IReduxState) => state.groupAccessMapping
  )
  const [isLoading, setIsLoading] = React.useState(true)
  const [isEditDialogOpen, setIsEditDialogOpen] = React.useState(false)
  const [isCloseDialogOpen, setIsCloseDialogOpen] = React.useState(false)
  const [isCancelDialogOpen, setIsCancelDialogOpen] = React.useState(false)
  const [alertMessage, setAlertMessage] = React.useState('')

  const handleSnackbarClose = (): void => {
    setIsFailureSnackbarOpen(false)
    setIsSuccessSnackbarOpen(false)
  }

  const [selectedPurchaseOrder, setSelectedPurchaseOrder] =
    React.useState<PurchaseOrder | null>(null)
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([])
  const [itemHistories, setItemHistories] = useState<{ [key: string]: any[] }>(
    {}
  )
  const [loadingHistories, setLoadingHistories] = useState<{
    [key: string]: boolean
  }>({})

  const [tabValue, setTabValue] = React.useState('Pending')

  const getPurchaseOrders = React.useCallback(async (status: string = tabValue) => {
    setIsLoading(true)
    const response = await API.get(
      'products',
      '/purchaseOrders/getPurchaseOrder',
      {
        queryStringParameters: {
          status,
        },
      }
    )
    const purchaseOrders = response.map(
      (purchaseOrder: any) => new PurchaseOrder(purchaseOrder)
    )
    setPurchaseOrders(purchaseOrders)
    setIsLoading(false)
  }, [tabValue])

  const [expandedRows, setExpandedRows] = React.useState<{
    [key: string]: boolean
  }>(
    purchaseOrders.reduce<{
      [key: string]: boolean
    }>((acc, purchaseOrder) => {
      acc[purchaseOrder.purchaseOrderId || ''] = false
      return acc
    }, {})
  )

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: string) => {
    setTabValue(newValue)
  }

  const handleCancelPurchaseOrder = async () => {
    if (!selectedPurchaseOrder) {
      return
    }

    try {
      await API.post(
        'products',
        `/purchaseOrders/cancelPurchaseOrder/${selectedPurchaseOrder.purchaseOrderId}`,
        {}
      )
      setAlertMessage(`Berhasil membatalkan PO`)
      setIsSuccessSnackbarOpen(true)
      await getPurchaseOrders()
    } catch (error) {
      setAlertMessage(`Gagal membatalkan PO`)
      setIsFailureSnackbarOpen(true)
    }
  }

  const [closePoNotes, setClosePoNotes] = React.useState('')
  const [closePoItems, setClosePoItems] = React.useState<any[]>([])
  React.useEffect(() => {
    if (isCloseDialogOpen && selectedPurchaseOrder) {
      setClosePoNotes(selectedPurchaseOrder.notes || '')
      setClosePoItems(
        selectedPurchaseOrder.purchaseOrderItems?.map((item) => ({
          ...item,
          notes: item.notes || '',
        })) || []
      )
    }
  }, [isCloseDialogOpen, selectedPurchaseOrder])

  const handleClosePurchaseOrder = async () => {
    if (!selectedPurchaseOrder) return
    try {
      await API.post(
        'products',
        `/purchaseOrders/closePurchaseOrder/${selectedPurchaseOrder.purchaseOrderId}`,
        {
          body: {
            notes: closePoNotes,
            items: closePoItems.map((item) => ({
              itemId: item.itemId,
              notes: item.notes,
            })),
          },
        }
      )
      setAlertMessage(`Berhasil menutup PO`)
      setIsSuccessSnackbarOpen(true)
      await getPurchaseOrders()
      setIsCloseDialogOpen(false)
    } catch (error) {
      setAlertMessage(`Gagal menutup PO`)
      setIsFailureSnackbarOpen(true)
    }
  }

  React.useEffect(() => {
    getPurchaseOrders(tabValue)
  }, [tabValue, getPurchaseOrders])

  const handleExpandRow = async (purchaseOrder: PurchaseOrder) => {
    const id = purchaseOrder.purchaseOrderId
      ? String(purchaseOrder.purchaseOrderId)
      : ''
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }))
    if (!itemHistories[id] && !loadingHistories[id]) {
      setLoadingHistories((prev) => ({ ...prev, [id]: true }))
      try {
        const result = await API.get(
          'products',
          `/purchaseOrders/purchaseOrderItemHistory/${id}`,
          {}
        )
        setItemHistories((prev) => ({ ...prev, [id]: result }))
      } catch (e) {
        setItemHistories((prev) => ({ ...prev, [id]: [] }))
      }
      setLoadingHistories((prev) => ({ ...prev, [id]: false }))
    }
  }

  const sortedPurchaseOrders = [...purchaseOrders].sort((a, b) => {
    const dateA = a.poDate
      ? new Date(a.poDate).getTime()
      : a.createdAt
        ? new Date(a.createdAt).getTime()
        : 0
    const dateB = b.poDate
      ? new Date(b.poDate).getTime()
      : b.createdAt
        ? new Date(b.createdAt).getTime()
        : 0
    return dateB - dateA
  })

  return (
    <Box>
      <Paper square>
        <Tabs
          scrollButtons="auto"
          value={tabValue}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
          variant="scrollable"
        >
          <Tab label={'Pending'} value={PENDING_STATUS} />
          <Tab label={'Cancelled'} value={CANCELLED_STATUS} />
          <Tab label={'Closed'} value={CLOSED_STATUS} />
          <Tab label={'Selesai'} value={COMPLETE_STATUS} />
        </Tabs>
      </Paper>
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="none"></TableCell>
                <TableCell>PO Number</TableCell>
                <TableCell>Supplier</TableCell>
                <TableCell>Tanggal PO</TableCell>
                <TableCell>Jenis</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Terakhir Diubah</TableCell>
                <TableCell>Tanggal PO Diinput</TableCell>
                <TableCell>PO Dibuat Oleh</TableCell>
                {tabValue === CANCELLED_STATUS && (
                  <TableCell>Tanggal PO Dicancel</TableCell>
                )}
                {tabValue === CLOSED_STATUS && (
                  <TableCell>Tanggal PO Ditutup</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={12}>
                    <Grid container justifyContent="center" alignItems="center">
                      <SmoothLoadingIndicator size={40} />
                    </Grid>
                  </TableCell>
                </TableRow>
              )}
              {purchaseOrders.length === 0 && !isLoading && (
                <TableRow>
                  <TableCell colSpan={12}>
                    <Typography>Tidak ada PO untuk diproses. </Typography>
                  </TableCell>
                </TableRow>
              )}
              {sortedPurchaseOrders.map((purchaseOrder) => (
                <React.Fragment>
                  <TableRow
                    className={classes.productRow}
                    hover
                    onClick={() => handleExpandRow(purchaseOrder)}
                  >
                    <TableCell padding="none">
                      <IconButton aria-label="expand row" size="small">
                        {expandedRows[purchaseOrder.purchaseOrderId ?? 0] ? (
                          <KeyboardArrowUpIcon />
                        ) : (
                          <KeyboardArrowDownIcon />
                        )}
                      </IconButton>
                    </TableCell>
                    <TableCell>{purchaseOrder.poNumber || '-'}</TableCell>
                    <TableCell>{purchaseOrder.supplier}</TableCell>
                    <TableCell>
                      {purchaseOrder.poDate?.toLocaleDateString()}
                    </TableCell>
                    <TableCell>{purchaseOrder.type || '-'}</TableCell>
                    <TableCell>{purchaseOrder.status || '-'}</TableCell>
                    <TableCell>
                      {purchaseOrder.lastModifiedEmployeeId || '-'}
                    </TableCell>
                    <TableCell>
                      {purchaseOrder.createdAt?.toLocaleDateString()}
                    </TableCell>
                    <TableCell>{purchaseOrder.createdBy || '-'}</TableCell>
                    {tabValue === CANCELLED_STATUS && (
                      <TableCell>
                        {purchaseOrder.cancelledDate?.toLocaleDateString()}
                      </TableCell>
                    )}
                    {tabValue === CLOSED_STATUS && (
                      <TableCell>
                        {purchaseOrder.closedDate?.toLocaleDateString()}
                      </TableCell>
                    )}
                  </TableRow>

                  <TableRow>
                    <TableCell
                      colSpan={12}
                      className={
                        expandedRows[purchaseOrder.purchaseOrderId ?? 0]
                          ? classes.hiddenDetails
                          : classes.expandableDetails
                      }
                    >
                      <Collapse
                        in={expandedRows[purchaseOrder.purchaseOrderId ?? 0]}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Box>
                          <TableContainer>
                            <Table>
                              <TableHead>
                                <TableRow>
                                  <TableCell>Kode Item</TableCell>
                                  <TableCell>Nama Item</TableCell>
                                  <TableCell>Notes</TableCell>
                                  <TableCell>Unit Price</TableCell>
                                  <TableCell>Jenis Pajak</TableCell>
                                  <TableCell>Diskon</TableCell>
                                  <TableCell>Qty Dibeli</TableCell>
                                  <TableCell>Qty Diterima</TableCell>
                                  <TableCell>Harga Total</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {purchaseOrder.purchaseOrderItems?.map(
                                  (poItem) => (
                                    <TableRow key={poItem.itemId}>
                                      <TableCell>{poItem.itemId}</TableCell>
                                      <TableCell>{poItem.itemName}</TableCell>
                                      <TableCell>
                                        {poItem.notes || '-'}
                                      </TableCell>
                                      <TableCell>{`Rp.${new Intl.NumberFormat(
                                        'id-ID'
                                      ).format(
                                        Number(poItem.unitPrice)
                                      )}`}</TableCell>
                                      <TableCell>{poItem.taxType}</TableCell>
                                      <TableCell>{`Rp.${new Intl.NumberFormat(
                                        'id-ID'
                                      ).format(
                                        Number(poItem.discount)
                                      )}`}</TableCell>
                                      <TableCell>
                                        {poItem.quantityPurchased}
                                      </TableCell>
                                      <TableCell>
                                        {poItem.quantityReceived ?? 0}
                                      </TableCell>
                                      <TableCell>{`Rp.${new Intl.NumberFormat(
                                        'id-ID',
                                        { maximumFractionDigits: 0 }
                                      ).format(
                                        Number(poItem.totalPrice)
                                      )}`}</TableCell>
                                    </TableRow>
                                  )
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          <Box mt={2}>
                            <Typography variant="subtitle1">
                              Riwayat Penerimaan Item
                            </Typography>
                            {loadingHistories[
                              String(purchaseOrder.purchaseOrderId) || ''
                            ] ? (
                              <Typography>Loading...</Typography>
                            ) : itemHistories[
                              String(purchaseOrder.purchaseOrderId) || ''
                            ] &&
                              itemHistories[
                                String(purchaseOrder.purchaseOrderId) || ''
                              ].length > 0 ? (
                              <TableContainer>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Kode Item</TableCell>
                                      <TableCell>Qty Diterima</TableCell>
                                      <TableCell>Tanggal Diterima</TableCell>
                                      <TableCell>Catatan</TableCell>
                                      <TableCell>Diterima Oleh</TableCell>
                                      <TableCell>Created At</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {(
                                      itemHistories[
                                      String(purchaseOrder.purchaseOrderId) ||
                                      ''
                                      ] || []
                                    ).map((h) => (
                                      <TableRow key={h.receiptHistoryId}>
                                        <TableCell>
                                          {h.purchaseOrderItemId}
                                        </TableCell>
                                        <TableCell>
                                          {h.receivedQuantity}
                                        </TableCell>
                                        <TableCell>
                                          {h.receivedDate
                                            ? new Date(
                                              h.receivedDate
                                            ).toLocaleDateString('id-ID')
                                            : '-'}
                                        </TableCell>
                                        <TableCell>
                                          {h.receivedNotes || '-'}
                                        </TableCell>
                                        <TableCell>
                                          {h.username || '-'}
                                        </TableCell>
                                        <TableCell>
                                          {h.createdAt
                                            ? new Date(
                                              h.createdAt
                                            ).toLocaleString('id-ID')
                                            : '-'}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            ) : (
                              <Typography color="textSecondary">
                                Tidak ada sejarah penerimaan item
                              </Typography>
                            )}
                          </Box>

                          {purchaseOrder.status === 'PENDING' && (
                            <Box mt={2}>
                              <Box display="inline" mr={2}>
                                <Button
                                  variant="contained"
                                  onClick={() => {
                                    setIsCancelDialogOpen(true)
                                    setSelectedPurchaseOrder(purchaseOrder)
                                  }}
                                  color="primary"
                                >
                                  Cancel
                                </Button>
                              </Box>
                              <Box display="inline" mr={2}>
                                <Button
                                  variant="contained"
                                  onClick={() => {
                                    setIsCloseDialogOpen(true)
                                    setSelectedPurchaseOrder(purchaseOrder)
                                  }}
                                  color="primary"
                                >
                                  Tutup
                                </Button>
                              </Box>
                              {checkAccess(
                                userGroups,
                                groupAccessMapping,
                                'purchaseOrder',
                                'modify'
                              ) && (
                                  <Box display="inline" mr={2}>
                                    <Button
                                      variant="contained"
                                      onClick={() => {
                                        setIsEditDialogOpen(true)
                                        setSelectedPurchaseOrder(purchaseOrder)
                                      }}
                                      color="secondary"
                                    >
                                      Edit
                                    </Button>
                                  </Box>
                                )}
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <Dialog
        fullWidth
        maxWidth="xl"
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
      >
        <DialogTitle>Edit Purchase Order</DialogTitle>
        <DialogContent>
          <ManagePurchaseOrder
            purchaseOrder={selectedPurchaseOrder!}
            closeDialog={() => {
              getPurchaseOrders()
              setIsEditDialogOpen(false)
            }}
          />
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        title="Batalkan Purchase Order"
        open={isCancelDialogOpen}
        setOpen={setIsCancelDialogOpen}
        onConfirm={handleCancelPurchaseOrder}
      >
        <DialogContent>
          <Typography variant="h6">
            Apakah yakin ingin batalkan PO No. {selectedPurchaseOrder?.poNumber}
            ?
          </Typography>
          <Typography variant="subtitle1">
            Jika item yang diterima rusak / kurang dan supplier tidak bisa
            memenuhi PO, dan kita belum bayar untuk PO ini, maka PO bisa di
            cancel. Jika item sudah diterima maka PO tidak bisa di cancel. Kalau
            barang rusak, maka keluarkan item tersebut sebagai adjustment lewat
            Request Kemasan Form dan Request Bahan Baku Form.
          </Typography>
        </DialogContent>
      </ConfirmDialog>
      <ClosePurchaseOrderDialog
        open={isCloseDialogOpen}
        onClose={() => setIsCloseDialogOpen(false)}
        purchaseOrder={selectedPurchaseOrder}
        onSubmit={(notes, items) => {
          setClosePoNotes(notes)
          setClosePoItems(items)
          handleClosePurchaseOrder()
        }}
      />
      <Snackbar
        open={isSuccessSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert severity="success" elevation={6} variant="filled">
          {alertMessage}
        </MuiAlert>
      </Snackbar>
      <Snackbar
        open={isFailureSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert severity="error" elevation={6} variant="filled">
          {alertMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
